import { Device } from '@/utils/Capacitor'
import firebase, { db, FieldValue, Timestamp } from '@/utils/Firebase'
import 'firebase/auth'

const StackTrace = require('stacktrace-js')

const ND = 'n/d'
const PRODUCTION = process.env.NODE_ENV === 'production'
const APP_NAME = process.env.VUE_APP_NAME || 'App'
const IS_BETA = process.env.VUE_APP_BETA === 'true'
const APP_VERSION = `${process.env.VUE_APP_VERSION || ND} (app ${process.env.VUE_APP_VERSION_APP || ND})${IS_BETA ? ' BETA' : ''}`

const IGNORE_PATTERNS = [
    'Navigation cancelled',
    'Navigation aborted',
    'Avoided redundant navigation',
    'Redirected when going from',
    'redirected from',
    'NavigationDuplicated',
    'ResizeObserver loop',
    'Unreachable hosts',
    'is not a child of this node',
    'The object can not be found here',
    'Translation Error',
    'Share canceled',
    'Share cancelled',
    'auth/network-request-failed',
    'Failed to fetch',
    'NetworkError when attempting to fetch',
    'client is offline',
    'Network Error',
    'Connection failed',
    'Indexed Database server',
    'in-progress transaction',
    'Document already exists',
    'storage/retry-limit-exceeded',
    'Upload has already been terminated',
    'Failed to get documents from server',
    'webkitPresentationMode',
    '__firefox__',
    'window.ethereum',
    'Failed to load Stripe.js',
]

let _store = null
let _router = null
let _platform = null
const _sent = new Set()

const LOGS_COLLECTION = 'errorLogs'
const MAX_LOGS_PER_SESSION = 30
let _loggedCount = 0

function fingerprint(parts) {
    const raw = parts.filter(Boolean).join('|')
    let hash = 0
    for (let i = 0; i < raw.length; i++) {
        hash = ((hash << 5) - hash) + raw.charCodeAt(i)
        hash |= 0
    }
    return Math.abs(hash).toString(36)
}

// Log agregado: un doc por error distinto y por dia (id = huella + fecha) con contador.
// Un error en loop suma al mismo doc en vez de escribir uno nuevo cada vez.
async function saveErrorLog(payload, fingerprintParts = null) {
    try {
        if (_loggedCount >= MAX_LOGS_PER_SESSION) return
        _loggedCount++

        const day = new Date().toISOString().slice(0, 10)
        const key = fingerprint(fingerprintParts || [payload.message, payload.route, payload.file, payload.line])
        const docId = `${day}_${key}`

        await db.collection(LOGS_COLLECTION).doc(docId).set({
            ...payload,
            day,
            count: FieldValue.increment(1),
            lastSeen: Timestamp.now(),
        }, { merge: true })
    } catch (e) {
        console.error('ErrorReporter log failed', e)
    }
}

async function getPlatform() {
    if (_platform) return _platform
    try { _platform = await Device.getPlatform() } catch (e) { _platform = ND }
    return _platform
}

function normalizeFileName(fileName) {
    if (!fileName) return ND
    const match = String(fileName).match(/\/(?:src|node_modules)\/.*/)
    return match ? match[0] : String(fileName)
}

function errorToString(error) {
    if (!error) return ND
    if (typeof error === 'string') return error
    const code = error.code ? ` (${error.code})` : ''
    return `${error.message || error.toString()}${code}`
}

export function isIgnorable(message) {
    return IGNORE_PATTERNS.some(p => message.includes(p))
}

const CHUNK_RELOAD_KEY = 'chunkReloadTs'
let _chunkErrorHandled = false
const CHUNK_ERROR_RE = /Loading (?:CSS )?chunk\s+\S+\s+failed|ChunkLoadError|error loading dynamically imported module|Importing a module script failed/i
const CALL_STACK_RE = /Maximum call stack size exceeded/i
const PERMISSION_DENIED_RE = /permission-denied|insufficient permissions/i
const ANONYMOUS_FRAME_RE = /^[\w$.]*@(?:\[native code\])?$/

function isChunkLoadError(error) {
    return CHUNK_ERROR_RE.test(errorToString(error))
}

function isInjectedScriptError(error, stackTrace) {
    if (stackTrace.length) return false
    const rawStack = String((error && error.stack) || '')
    const frames = rawStack.match(/(?:https?|capacitor|ionic|file):\/\/\S+/g)
    if (frames) return frames.every(frame => !frame.includes('.js'))
    const anonymousFrames = rawStack.split('\n').map(line => line.trim()).filter(Boolean)
    return anonymousFrames.length > 1 && anonymousFrames.every(line => ANONYMOUS_FRAME_RE.test(line))
}

async function dropServiceWorkerCaches() {
    try {
        if (navigator.serviceWorker) {
            const registrations = await navigator.serviceWorker.getRegistrations()
            await Promise.all(registrations.map(registration => registration.unregister()))
        }
        if (window.caches) {
            const keys = await window.caches.keys()
            await Promise.all(keys.map(key => window.caches.delete(key)))
        }
    } catch (e) { /* ignore */ }
}

async function reloadForChunkError() {
    try {
        if (typeof window === 'undefined') return false
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return false
        const now = Date.now()
        const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0)
        if (now - last < 20000) return false
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now))
        await dropServiceWorkerCaches()
        window.location.reload()
        return true
    } catch (e) {
        return false
    }
}

function getContext(vm) {
    const state = (_store && _store.state) || {}
    const currentUser = (vm && vm.currentUser) || state.currentUser || null
    const account = (vm && vm.account) || state.account || null
    let accountType = (account && account.accountType) || ''
    if (accountType === 'coach_company') accountType = 'coach'
    const route = (vm && vm.$route) || (_router && _router.currentRoute) || null
    return { currentUser, accountType, route }
}

function buildStackTraceString(stackTrace) {
    let str = ''
    stackTrace.forEach((stack, i) => {
        str += `\n ${i + 1}- File: ${normalizeFileName(stack.fileName)}`
        str += `\n    Function: ${stack.functionName || ND}`
        str += `\n    Line: ${stack.lineNumber != null ? stack.lineNumber : ND} \n`
    })
    return str
}

function buildAuthInfo(label = 'Auth') {
    try {
        const user = firebase.auth().currentUser
        if (!user) return `${label}: SIN USUARIO (sesion perdida) \n`

        const manager = (user.toJSON() || {}).stsTokenManager || {}
        const expiration = Number(manager.expirationTime || 0)
        const minutes = Math.round((expiration - Date.now()) / 60000)
        const token = !expiration
            ? 'sin expirationTime'
            : (expiration > Date.now() ? `vigente (vence en ${minutes} min)` : `VENCIDO hace ${Math.abs(minutes)} min`)

        return `${label}: uid ${user.uid} \nToken: ${token} \nRefresh token: ${manager.refreshToken ? 'si' : 'NO'} \n`
    } catch (e) {
        return `${label}: no se pudo leer \n`
    }
}

function componentName(vm) {
    return (vm && vm.$options && (vm.$options.name || vm.$options._componentTag)) || null
}

function buildComponentPath(vm) {
    const names = []
    let current = vm && vm.$parent
    while (current && names.length < 8) {
        const name = componentName(current)
        if (name && names[names.length - 1] !== name) names.push(name)
        current = current.$parent
    }
    return names.length ? names.join(' < ') : null
}

function buildElementText(vm) {
    const text = vm && vm.$el && vm.$el.innerText
    if (!text) return null
    return String(text).replace(/\s+/g, ' ').trim().substr(0, 60) || null
}

function captureErrorContext(error, vm, vueInfo) {
    const context = {}
    try {
        context.uptimeSeconds = typeof performance !== 'undefined' ? Math.round(performance.now() / 1000) : null
        if (PERMISSION_DENIED_RE.test(errorToString(error))) context.authAtError = buildAuthInfo('Auth al error')
        if (vm && vm.$options) context.component = vm.$options.name || vm.$options._componentTag || null
        context.componentPath = buildComponentPath(vm)
        context.elementText = buildElementText(vm)
        if (vueInfo) context.vueInfo = String(vueInfo)
    } catch (e) { /* ignore */ }
    return context
}

function buildMoreInfo(vm, routeName, stackTrace, error, context = {}) {
    let moreInfo = ''
    const firstFile = normalizeFileName(stackTrace[0] && stackTrace[0].fileName)

    if (context.authAtError) moreInfo += context.authAtError
    if (PERMISSION_DENIED_RE.test(errorToString(error))) moreInfo += buildAuthInfo('Auth al reportar')
    if (context.uptimeSeconds !== null && context.uptimeSeconds !== undefined) moreInfo += `Segundos desde carga: ${context.uptimeSeconds} \n`
    if (context.component) moreInfo += `Componente: ${context.component} \n`
    if (context.componentPath) moreInfo += `Padres: ${context.componentPath} \n`
    if (context.elementText) moreInfo += `Texto: ${context.elementText} \n`
    if (context.vueInfo) moreInfo += `Vue: ${context.vueInfo} \n`

    if (routeName === 'coach.calendar' && vm) {
        moreInfo += `Estudiante: ${(vm.student && vm.student.name) || ND} \n`
        if (firstFile === '/src/components/common/routines/CopyRoutineDialog.vue') {
            moreInfo += `Fecha a copiar: ${(vm.selectDate && JSON.stringify(vm.selectDate)) || ND} \n`
            moreInfo += `Ejercicio: ${(vm.exercise && vm.exercise.name) || ND} \n`
            if (vm.students && vm.students.length > 0) {
                moreInfo += `Estudiantes a copiar: ${JSON.stringify(vm.students).substr(0, 700)} \n`
            }
        } else if (firstFile === '/src/components/coach/routines/AddExerciseDesktopDialog.vue') {
            moreInfo += `Categoria: ${(vm.category && vm.category.name) || ND} \n`
            moreInfo += `Ejercicio: ${(vm.exercise && vm.exercise.name) || ND} \n`
            moreInfo += `Fecha: ${(vm.date && vm.date.name) || ND} \n`
            moreInfo += `Modo edicion: ${vm.editMode ? 'Si' : 'No'} \n`
        }
    }

    const isChatError = stackTrace.some(s => (s.fileName || '').includes('ChatDialog'))
    if (isChatError && vm) {
        moreInfo += `chatId: ${JSON.stringify(vm.chatId)} \n`
        moreInfo += `clientId: ${vm.clientId || ND} \n`
        moreInfo += `collectionName: ${vm.collectionName || ND} \n`
        moreInfo += `firstMessage: ${vm.firstMessage} \n`
        moreInfo += `isCompanyChat: ${vm.isCompanyChat} \n`
        moreInfo += `accountType: ${vm.accountType || ND} \n`
        moreInfo += `currentUser: ${(vm.currentUser && vm.currentUser.uid) || ND} \n`
    }

    if (vm && typeof vm.errorContext === 'function') {
        try {
            const extra = vm.errorContext()
            if (extra) moreInfo += `${typeof extra === 'string' ? extra : JSON.stringify(extra)} \n`
        } catch (e) { /* ignore */ }
    }

    return moreInfo || ND
}

async function report(error, vm, source = 'vue', fallbackLocation = null, vueInfo = null) {
    const context = captureErrorContext(error, vm, vueInfo)
    try {
        if (PRODUCTION && isChunkLoadError(error)) {
            if (_chunkErrorHandled) return
            _chunkErrorHandled = true
            if (typeof navigator !== 'undefined' && navigator.onLine === false) return
            if (await reloadForChunkError()) return
        }
        if (!PRODUCTION) return

        const errorMessage = errorToString(error)
        if (isIgnorable(errorMessage)) return

        let stackTrace = []
        try {
            if (error instanceof Error) stackTrace = await StackTrace.fromError(error)
        } catch (e) { /* ignore */ }

        if (stackTrace.some(s => /^(?:chrome|moz|safari-web)-extension:\/\//.test(s.fileName || ''))) return
        if (isInjectedScriptError(error, stackTrace)) return

        const touchesOwnCode = stackTrace.some(s => (s.fileName || '').includes('/src/'))
        if (CALL_STACK_RE.test(errorMessage) && !touchesOwnCode) return

        const { currentUser, accountType, route } = getContext(vm)
        const routeName = (route && route.name) || ND

        if (!currentUser && PERMISSION_DENIED_RE.test(errorMessage)) return

        const dedupeKey = `${routeName}|${errorMessage}|${(stackTrace[0] && stackTrace[0].lineNumber) || ''}`
        if (_sent.has(dedupeKey)) return
        _sent.add(dedupeKey)
        if (_sent.size > 200) _sent.clear()

        const email = (currentUser && currentUser.email) || '(Sin Usuario)'
        const uid = (currentUser && currentUser.uid) || ND
        const routeParams = (route && route.params) ? JSON.stringify(route.params) : ND
        const platform = await getPlatform()
        const stackTraceString = stackTrace.length ? buildStackTraceString(stackTrace) : ((error && error.stack) || ND)
        const moreInfo = buildMoreInfo(vm, routeName, stackTrace, error, context)

        await saveErrorLog({
            app: APP_NAME,
            message: errorMessage.substr(0, 1024),
            route: routeName,
            file: normalizeFileName((stackTrace[0] && stackTrace[0].fileName) || (fallbackLocation && fallbackLocation.file)),
            line: (stackTrace[0] && stackTrace[0].lineNumber) || (fallbackLocation && fallbackLocation.line) || null,
            source,
            platform,
            version: APP_VERSION,
            email,
            uid,
            accountType: accountType || ND,
            params: routeParams.substr(0, 512),
            moreInfo: moreInfo.substr(0, 1024),
            stack: String(stackTraceString).substr(0, 2048),
        })
    } catch (e) {
        console.error('ErrorReporter report failed', e)
    }
}

export async function reportDiagnostic({ title = 'Diagnostico', fields = {} } = {}) {
    try {
        if (!PRODUCTION) return

        const serializedFields = Object.keys(fields)
            .map(name => `${name}: ${String(fields[name])}`)
            .join('\n')

        const dedupeKey = `diag|${title}|${serializedFields}`
        if (_sent.has(dedupeKey)) return
        _sent.add(dedupeKey)
        if (_sent.size > 200) _sent.clear()

        const { currentUser, accountType, route } = getContext(null)
        const routeName = (route && route.name) || ND
        const platform = await getPlatform()

        await saveErrorLog({
            app: APP_NAME,
            message: title.substr(0, 1024),
            route: routeName,
            file: null,
            line: null,
            source: 'diagnostic',
            platform,
            version: APP_VERSION,
            email: (currentUser && currentUser.email) || '(Sin Usuario)',
            uid: (currentUser && currentUser.uid) || ND,
            accountType: accountType || ND,
            params: ND,
            moreInfo: serializedFields.substr(0, 1024) || ND,
            stack: ND,
        }, ['diagnostic', title, serializedFields])
    } catch (e) {
        console.error('ErrorReporter diagnostic failed', e)
    }
}

export function setupErrorReporting(Vue, { store, router } = {}) {
    _store = store || null
    _router = router || null

    Vue.config.errorHandler = (error, vm, info) => {
        console.error('errorHandler error', error, info)
        report(error, vm, 'vue', null, info)
    }

    if (typeof window !== 'undefined') {
        window.addEventListener('unhandledrejection', (event) => {
            report((event && event.reason) || new Error('unhandledrejection'), null, 'promise')
        })
        window.addEventListener('error', (event) => {
            if (!event || !(event.error instanceof Error)) return
            report(event.error, null, 'window', { file: event.filename, line: event.lineno })
        })
    }
}
